import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { useTheme } from "@mui/material";
import {
  AVG_LIGHTNESS,
  DEFAULT_HUE,
  DEFAULT_SATURATION,
  MAX_LIGHTNESS,
  MIN_LIGHTNESS,
} from "@pages/platform-analysis/_id/steps/fee/analysis/components/heatmap/config";
import { PDFLabelLineDetailV2 } from "@pages/platform-analysis/components/buttons/export-pdf/components/heatmap/components/label-line-detail-v2";
import { PDFLabelLineProjection } from "@pages/platform-analysis/components/buttons/export-pdf/components/heatmap/components/label-line-projection";
import { PDFVerticalDashLine } from "@pages/platform-analysis/components/buttons/export-pdf/components/heatmap/components/vertical-dash-line";
import { useStyles } from "@pages/platform-analysis/components/buttons/export-pdf/styles";
import {
  displayCurrencyNumber,
  getFeeDetailMinMaxExcludePerformanceFees,
  getFeeMinMaxExcludePerformanceFees,
  removeSubProductNameForSpecificProduct,
} from "@pages/platform-analysis/util";
import { Text, View } from "@react-pdf/renderer";
import {
  formatPercentage,
  getHSLLightness,
  hslToHex,
  thousandSeparator,
} from "@utils";
import { defaultTo, isNull } from "lodash";
import { TITLE_HEIGHT } from "../../../constant";
import { HorizontalLine } from "../../line";
import { PDFFeeSubProductCardV2 } from "../../sub-product-card-v2";
import { PDFFeeAnalysisConfig } from "./configs";
import { calculateFeeDetailColumnWidth } from "./utils";
import { PDFVerticalDashLineV2 } from "../../heatmap/components/vertical-dash-line-v2";
import { LineChartLegendConstants } from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";

export interface PDFFeeAnalysisSectionProps {
  totalSubProductHidden: number;
  subProducts: FeeSubProductDTO[];
  totalPortfolioValue?: number;
  mode?: FeesDisplayStyle;
  heatmapMode?: FeeHeatmapMode;
  color?: {
    hue: number;
    saturation: number;
  };
  minLightness?: number;
  maxLightness?: number;
  avgLightness?: number;
  captions?: {
    dollar: string;
    percentage: string;
  };
  showWarningCaptions?: boolean;
  isContinue?: boolean;
  showFeeAnalysisText?: boolean;
  showFeeEstimatesText?: boolean;
  showAnnualPlatformFeesText?: boolean;
  isFamilyLevel?: boolean;
  feesPerformanceScope?: FeesPerformanceScope;
  isExcludePerformanceFees?: boolean;
}

function getSubtitleText({
  isFamilyLevel,
  isProjectionMode,
  isIncludePerf,
  isContinue,
}: {
  isFamilyLevel?: boolean;
  isProjectionMode: boolean;
  isIncludePerf: boolean;
  isContinue: boolean;
}) {
  if (isFamilyLevel) {
    if (!isProjectionMode) {
      return isIncludePerf
        ? `Annual platform fees and SMA costs (estimates)${
            isContinue ? " Continued" : ""
          }`
        : `Annual platform fees and SMA costs (estimates excluding performance fees)${
            isContinue ? " Continued" : ""
          }`;
    } else {
      return isIncludePerf
        ? `Annual platform and SMA costs by portfolio value (estimates based on original portfolio allocation)${
            isContinue ? " Continued" : ""
          }`
        : `Annual platform and SMA costs by portfolio value (estimates based on original portfolio allocation, excluding performance fees)${
            isContinue ? " Continued" : ""
          }`;
    }
  } else {
    return `Annual platform fees (estimates)${isContinue ? " Continued" : ""}`;
  }
}

export const PDFFeeAnalysisSection = ({
  totalSubProductHidden,
  subProducts,
  totalPortfolioValue,
  mode = FeesDisplayStyle.Dollar,
  heatmapMode = FeeHeatmapMode.Projection,
  color = {
    hue: DEFAULT_HUE,
    saturation: DEFAULT_SATURATION,
  },
  minLightness = MIN_LIGHTNESS,
  maxLightness = MAX_LIGHTNESS,
  avgLightness = AVG_LIGHTNESS,
  captions = {
    dollar: "Total fees",
    percentage: "of total portfolio value",
  },
  showWarningCaptions,
  isContinue = false,
  showFeeAnalysisText = false,
  showFeeEstimatesText = false,
  isFamilyLevel,
  feesPerformanceScope,
  isExcludePerformanceFees = false,
}: PDFFeeAnalysisSectionProps) => {
  const { palette } = useTheme();
  const { typographyStyle } = useStyles();
  const isDollarMode = mode === FeesDisplayStyle.Dollar;
  const isProjectionMode = heatmapMode === FeeHeatmapMode.Projection;
  const isIncludePerf = feesPerformanceScope === FeesPerformanceScope.Including;
  const hasTooManyFeeColumns = subProducts.some(
    (subProduct) =>
      subProduct.feeDetails.length >
      PDFFeeAnalysisConfig.MaxFeeDetailsColumnToDisplayLogo,
  );
  const contentWidth = hasTooManyFeeColumns
    ? PDFFeeAnalysisConfig.FeeDetailContentWidthWithoutLogo
    : PDFFeeAnalysisConfig.FeeDetailContentWidth;

  const removedSubProductNameForSpecificProduct =
    removeSubProductNameForSpecificProduct(subProducts ?? []);

  const renderProjection = () => {
    const firstProductFees = subProducts[0]?.fees ?? [];
    const { min, max } = getFeeMinMaxExcludePerformanceFees({
      subProducts,
      isDollarMode,
      isExcludePerformanceFees,
    });

    return (
      <View style={{ width: PDFFeeAnalysisConfig.FeeDetailContentWidth }}>
        <PDFLabelLineProjection firstProductFees={firstProductFees} />
        <PDFVerticalDashLine colNumber={firstProductFees?.length} />
        <View style={{ flexDirection: "row" }}>
          <View style={{ justifyContent: "space-between" }}>
            {Array.from(Array(subProducts.length + 1).keys()).map((index) => (
              <View
                key={index}
                style={{
                  width: 3,
                  height: 0.5,
                  backgroundColor: palette.text.primary,
                  transform:
                    index === 0
                      ? "translateY(1.5px)"
                      : index === subProducts.length
                      ? "translateY(-1.5px)"
                      : undefined,
                }}
              />
            ))}
          </View>
          <View
            style={{
              gap: 1,
            }}
          >
            {subProducts.map((subProduct, subProductIndex) => (
              <View key={subProductIndex} style={{ flexDirection: "row" }}>
                {subProduct.fees.map((fee, index) => {
                  const displayDollarValue = isExcludePerformanceFees
                    ? defaultTo(
                        fee.totalCostExcPerfForDisplay,
                        fee.totalCostForDisplay,
                      )
                    : fee.totalCostForDisplay;
                  const displayPercentageValue = isExcludePerformanceFees
                    ? defaultTo(
                        fee.totalCostExcPerfForDisplayPercentage,
                        fee.totalCostForDisplayPercentage,
                      )
                    : fee.totalCostForDisplayPercentage;

                  const lightness = getHSLLightness(
                    min,
                    max,
                    isDollarMode ? displayDollarValue : displayPercentageValue,
                    minLightness,
                    maxLightness,
                  );
                  const isDarkCell = lightness < avgLightness;
                  const displayData = isDollarMode
                    ? `$${thousandSeparator(+displayDollarValue.toFixed(0))}`
                    : `${formatPercentage(displayPercentageValue)}%`;

                  return (
                    <View
                      style={{
                        flexDirection: "column",
                        justifyContent: "center",
                        alignContent: "center",
                        height: 30,
                        width:
                          PDFFeeAnalysisConfig.FeeDetailContentWidth /
                          subProduct?.fees?.length,
                        backgroundColor: hslToHex(
                          color.hue,
                          color.saturation,
                          lightness,
                        ),
                        margin: "1.5px",
                      }}
                      key={index}
                    >
                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          color: isDarkCell
                            ? palette.common.white
                            : palette.text.darker,
                          textAlign: "center",
                          fontSize: 7,
                        }}
                      >
                        {displayData}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <PDFVerticalDashLine colNumber={firstProductFees?.length} />
        <PDFLabelLineProjection firstProductFees={firstProductFees} />

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: showWarningCaptions ? "flex-start" : "center",
            marginTop: 10,
          }}
        >
          <View style={{ gap: 7 }}>
            <View
              style={{
                flexDirection: "row",
                gap: 2,
                marginTop: "2px",
              }}
            >
              <Text style={{ ...typographyStyle?.body2 }}>
                {isDollarMode ? " $" : "%"}
              </Text>
              <Text style={{ ...typographyStyle?.body2, fontWeight: "bold" }}>
                {isDollarMode ? captions.dollar : captions.percentage}
              </Text>
            </View>

            {showWarningCaptions && (
              <View
                style={{
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.error.main,
                  }}
                >
                  * Product is missing some investment options, see web version
                  of this report for full details
                </Text>
              </View>
            )}
          </View>
          <View
            style={{
              backgroundColor: "#e0e2e5",
              width: 180,
              minWidth: 180,
              height: 10,
              alignItems: "center",
              justifyContent: "center",
              border: 0.5,
              borderColor: "#b1b6bf",
            }}
          >
            <Text style={{ ...typographyStyle.body2 }}>
              {LineChartLegendConstants.XAxis}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderDetails = () => {
    if (!subProducts[0]?.feeDetails) return null;
    const firstProductFeeDetails = subProducts[0]?.feeDetails ?? [];
    const arrayMinMax = getFeeDetailMinMaxExcludePerformanceFees({
      subProducts,
      isDollarMode,
      isExcludePerformanceFees,
    });

    const { pysColumnWidth, otherColumnWidth } = calculateFeeDetailColumnWidth(
      contentWidth,
      firstProductFeeDetails,
    );

    return (
      <View style={{ width: contentWidth }}>
        <PDFLabelLineDetailV2
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
          isExcludePerformanceFees={isExcludePerformanceFees}
        />
        <PDFVerticalDashLineV2
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
        />

        <View style={{ flexDirection: "row" }}>
          <View style={{ justifyContent: "space-between" }}>
            {Array.from(Array(subProducts.length + 1).keys()).map((index) => (
              <View
                key={index}
                style={{
                  width: 3,
                  height: 0.5,
                  backgroundColor: palette.text.primary,
                  transform:
                    index === 0
                      ? "translateY(1.5px)"
                      : index === subProducts.length
                      ? "translateY(-1.5px)"
                      : undefined,
                }}
              />
            ))}
          </View>
          <View
            style={{
              gap: 1,
            }}
          >
            {subProducts.map((subProduct, subProductIndex) => (
              <View key={subProductIndex} style={{ flexDirection: "row" }}>
                {subProduct.feeDetails.map((feeDetail, index) => {
                  const { min, max } = arrayMinMax[index] ?? {
                    min: 0,
                    max: 0,
                  };
                  const dollarValueToRender = !isExcludePerformanceFees
                    ? feeDetail.dollar
                    : defaultTo(feeDetail.dollarExcPerf, feeDetail.dollar);
                  const percentageValueToRender = !isExcludePerformanceFees
                    ? feeDetail.percentage
                    : defaultTo(
                        feeDetail.percentageExcPerf,
                        feeDetail.percentage,
                      );
                  const lightness = getHSLLightness(
                    min,
                    max,
                    isDollarMode
                      ? dollarValueToRender || 0
                      : percentageValueToRender || 0,
                    minLightness,
                    maxLightness,
                  );
                  const isDarkCell = lightness < avgLightness;

                  const displayData =
                    isNull(percentageValueToRender) ||
                    isNull(dollarValueToRender)
                      ? "No data"
                      : isDollarMode
                      ? `$${thousandSeparator(+dollarValueToRender.toFixed(0))}`
                      : `${formatPercentage(percentageValueToRender)}%`;

                  return (
                    <View
                      style={{
                        flexDirection: "column",
                        justifyContent: "center",
                        alignContent: "center",
                        height: 30,
                        width: feeDetail.isPysRefund
                          ? pysColumnWidth
                          : otherColumnWidth,
                        backgroundColor: hslToHex(
                          color.hue,
                          color.saturation,
                          lightness,
                        ),
                        marginVertical: "1.5px",
                        marginRight: index === 0 ? "5px" : "1.5px",
                        marginLeft:
                          index === subProduct.feeDetails.length - 1
                            ? "5px"
                            : "1.5px",
                      }}
                      key={index}
                    >
                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          color: isDarkCell
                            ? palette.common.white
                            : palette.text.darker,
                          textAlign: "center",
                          fontSize: 7,
                        }}
                      >
                        {displayData}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <PDFVerticalDashLineV2
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
        />
        <PDFLabelLineDetailV2
          firstProductFeeDetails={firstProductFeeDetails}
          hasTooManyFeeColumns={hasTooManyFeeColumns}
          isExcludePerformanceFees={isExcludePerformanceFees}
        />

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: showWarningCaptions ? "flex-start" : "center",
            marginTop: 10,
          }}
        >
          <View style={{ gap: 7 }}>
            <View
              style={{
                flexDirection: "row",
                gap: 2,
                marginTop: "2px",
              }}
            >
              <Text style={{ ...typographyStyle?.body2 }}>
                {isDollarMode ? " $" : "%"}
              </Text>
              <Text style={{ ...typographyStyle?.body2, fontWeight: "bold" }}>
                {`Fees for a total balance of ${displayCurrencyNumber(
                  totalPortfolioValue ?? 0,
                )}`}
              </Text>
            </View>

            {showWarningCaptions && (
              <View
                style={{
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.error.main,
                  }}
                >
                  * Product is missing some investment options, see web version
                  of this report for full details
                </Text>
              </View>
            )}
          </View>
          <View
            style={{
              backgroundColor: "#e0e2e5",
              width: 60,
              minWidth: 60,
              height: 10,
              alignItems: "center",
              justifyContent: "center",
              border: 0.5,
              borderColor: "#b1b6bf",
            }}
          >
            <Text style={{ ...typographyStyle.body2 }}>Fee Type</Text>
          </View>
        </View>
      </View>
    );
  };

  const hasData =
    subProducts.length &&
    (isProjectionMode
      ? subProducts[0].fees?.length
      : subProducts[0].feeDetails?.length);

  let titleHeight = TITLE_HEIGHT;
  if (showFeeAnalysisText) {
    titleHeight += TITLE_HEIGHT;
    if (!isProjectionMode && !showFeeEstimatesText) {
      titleHeight -= 15;
    }
  }

  if (isProjectionMode)
    titleHeight -=
      PDFFeeAnalysisConfig.ReduceFeeColumnHeight * (isFamilyLevel ? -1 : 1);

  if (subProducts[0]?.feeDetails.length > 4 && !isProjectionMode)
    titleHeight += 5;

  return (
    <View
      style={{
        gap: 25,
        // border: `1px solid ${palette.secondary[100]}`,
        width: "100%",
      }}
    >
      <View
        style={{
          gap: `${showFeeEstimatesText} ? "5px" : "0px"`,
          height: titleHeight,
        }}
      >
        {showFeeAnalysisText && (
          <View>
            <Text
              style={{
                ...typographyStyle.title,
                color: palette.common.black,
              }}
            >
              Fee analysis (continued)
            </Text>
            <HorizontalLine color={palette.common.black} />
          </View>
        )}
        {showFeeEstimatesText && (
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
              marginBottom: "2px",
            }}
          >
            Fee estimates
          </Text>
        )}

        <Text
          style={{
            ...typographyStyle.subtitle,
            color: palette.common.black,
          }}
        >
          {getSubtitleText({
            isFamilyLevel,
            isProjectionMode,
            isIncludePerf,
            isContinue,
          })}
        </Text>
      </View>

      {hasData ? (
        <View
          style={{
            gap: 5,
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <View
            style={{
              gap: 4,
              flexDirection: "column",
              marginTop: isProjectionMode ? 13 : 30,
            }}
          >
            {removedSubProductNameForSpecificProduct.map(
              (subProduct, subProductIndex) => (
                <View
                  key={subProductIndex}
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 5,
                    height: 30,
                  }}
                >
                  <PDFFeeSubProductCardV2 subProduct={subProduct} />
                </View>
              ),
            )}
            <View
              style={{
                height: 10,
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: !isProjectionMode ? "37px" : "20px",
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: "#1E1970",
                  fontWeight: "bold",
                }}
              >
                {totalSubProductHidden > 1
                  ? `${totalSubProductHidden} products hidden`
                  : totalSubProductHidden === 1
                  ? `${totalSubProductHidden} product hidden`
                  : ""}
              </Text>
            </View>
          </View>
          {isProjectionMode ? renderProjection() : renderDetails()}
        </View>
      ) : (
        <View style={{ gap: "5px" }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
              marginTop: "25px",
            }}
          >
            {totalSubProductHidden > 1
              ? `${totalSubProductHidden} products hidden`
              : totalSubProductHidden === 1
              ? `${totalSubProductHidden} product hidden`
              : ""}
          </Text>
        </View>
      )}
    </View>
  );
};
